import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getEnsayo, setSesionLocal } from '../api/ensayosClient';

/**
 * Página participante del ensayo — paridad con /Home/Secuencias?cod= del sitio viejo.
 * Ruta: /secuencias?cod=XXXXXXXX
 */
export default function EnsayoSecuencias() {
  const [params] = useSearchParams();
  const cod = (params.get('cod') ?? '').trim().toUpperCase();
  const [ensayo, setEnsayo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!cod) {
      setError('Falta el código del ensayo.');
      setLoading(false);
      return;
    }
    setLoading(true);
    getEnsayo(cod)
      .then((data) => {
        setEnsayo(data);
        setSesionLocal(data.code, data.sujeto);
        setError('');
      })
      .catch((e) => {
        setEnsayo(null);
        setError(e.message || 'El código ingresado es incorrecto');
      })
      .finally(() => setLoading(false));
  }, [cod]);

  const tareas = ensayo?.lista_tareas ?? [];
  const next = tareas.find((t) => !t.completed && t.link);
  const allDone = tareas.length > 0 && tareas.every((t) => t.completed);

  // Parse "Nombre [sesión X]" como el sitio viejo
  const sujetoNombre = (() => {
    if (!ensayo?.sujeto) return '';
    const m = ensayo.sujeto.match(/^(.*?)\s*\[(.+)\]\s*$/);
    return m ? m[1].trim() : ensayo.sujeto;
  })();
  const sujetoSession = (() => {
    if (!ensayo?.sujeto) return '';
    const m = ensayo.sujeto.match(/^(.*?)\s*\[(.+)\]\s*$/);
    return m ? m[2].trim() : '';
  })();

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <div className="bg-[#222] text-white py-3 px-4">
        <Link to="/" className="inline-flex items-center gap-2 no-underline text-white">
          <img src="/assets/img/logo.png" alt="" className="h-9 w-9 object-contain" />
          <img src="/assets/img/logo-letra-blanco.png" alt="LABPSI" className="h-5 object-contain" />
        </Link>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {loading && <p className="text-slate-500 text-center">Cargando...</p>}

        {!loading && error && (
          <div className="text-center">
            <h2 className="text-2xl font-normal text-slate-700 mb-2">El código ingresado es incorrecto</h2>
            <p className="text-slate-500 mb-6">Por favor, contacte al administrador para resolver el problema</p>
            <p className="text-sm text-red-500 mb-6">{error}</p>
            <Link
              to="/"
              className="inline-block px-6 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white no-underline font-medium"
            >
              Ir a Inicio
            </Link>
          </div>
        )}

        {!loading && ensayo && (
          <>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-normal m-0">
                <span className="text-slate-500 text-xl block mb-1">Hola</span>
                <b>{sujetoNombre}</b>
              </h1>
              {sujetoSession && (
                <p className="text-slate-500 mt-2 mb-0">{sujetoSession}</p>
              )}
            </div>

            {ensayo.text && (
              <div className="mb-6">
                <h4 className="text-lg font-normal text-slate-700 whitespace-pre-wrap m-0">{ensayo.text}</h4>
              </div>
            )}

            <hr className="border-slate-200 mb-6" />

            <div className="mb-8">
              <h4 className="text-xl font-normal text-slate-700 mb-4 flex items-center gap-3">
                <img src="/assets/img/l_4.png" alt="" className="h-12 w-auto" />
                Tareas a realizar ({tareas.length}):
              </h4>
              <ol className="list-none m-0 p-0 space-y-2 pl-4">
                {tareas.map((t, i) => (
                  <li key={t.code + i} className="text-lg flex items-start gap-2">
                    {t.completed ? (
                      <span className="text-[#7f4c88] mt-0.5" aria-hidden>✓</span>
                    ) : (
                      <span className="w-4 inline-block" />
                    )}
                    <span>
                      <b>{i + 1}. {t.name}</b>
                      {t.description && (
                        <small className="text-slate-500 ml-2">({t.description})</small>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
              {tareas.length === 0 && (
                <p className="text-slate-500">Este ensayo no tiene tareas asignadas.</p>
              )}
            </div>

            <div className="text-center">
              {allDone && (
                <div className="text-lg font-medium text-[#7f4c88] py-4">
                  TODAS LAS TAREAS FUERON REALIZADAS
                </div>
              )}
              {!allDone && next && (
                <a
                  href={next.link}
                  className="inline-block px-8 py-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white no-underline leading-tight"
                >
                  <small className="block opacity-90">Ir a próxima tarea</small>
                  <b className="text-lg">{next.name}</b>
                </a>
              )}
              {!allDone && !next && tareas.length > 0 && (
                <p className="text-amber-700 text-sm">
                  Hay tareas pendientes pero no se pudo armar el link. Revisá los códigos en el admin.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
